import React from 'react';
import { MapPin, DollarSign, Calendar, Star, Power, PowerOff, Edit, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ProjectListView - Compact list view for projects
 */
const ProjectListView = ({ 
  projects, 
  isOwner = false,
  onToggleActivation,
  onEdit,
  onDelete,
  onNavigate,
  favorites = new Set(),
  onToggleFavorite
}) => {
  if (projects.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No projects found
      </div>
    );
  }

  const formatBudget = (project) => {
    if (project.price_amount) {
      return `${project.currency || 'EUR'} ${project.price_amount}${project.price_type ? `/${project.price_type}` : ''}`;
    }
    if (project.budget) {
      return `${project.currency || 'EUR'} ${project.budget}`;
    }
    return 'On request';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
        <div className="col-span-3">Project Title</div>
        <div className="col-span-2">Location</div>
        <div className="col-span-2">Budget</div>
        <div className="col-span-2">Start Date</div>
        <div className="col-span-2">Duration</div>
        <div className="col-span-1 text-right">Actions</div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100">
        {projects.map((project) => (
          <div 
            key={project.id}
            className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer items-center"
            onClick={() => onNavigate && onNavigate(project.id)}
            data-testid={`project-row-${project.id}`}
          >
            {/* Title */}
            <div className="col-span-3">
              <div className="flex items-center gap-2">
                {isOwner && (
                  <span 
                    className={`w-2 h-2 rounded-full ${project.is_activated ? 'bg-green-500' : 'bg-gray-300'}`}
                    title={project.is_activated ? 'Active' : 'Inactive'}
                  />
                )}
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{project.title}</p>
                  <p className="text-sm text-gray-500 truncate">
                    {project.project_type || 'Implementation'}
                    {project.number_of_consultants > 1 && (
                      <span className="ml-2 inline-flex items-center">
                        <Users className="w-3 h-3 mr-0.5" />
                        {project.number_of_consultants}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="col-span-2">
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">
                  {project.city || project.country || (project.remote_possible ? 'Remote' : 'TBD')}
                </span>
              </div>
            </div>

            {/* Budget */}
            <div className="col-span-2">
              <div className="flex items-center text-sm">
                <DollarSign className="w-3 h-3 mr-1 text-gray-400" />
                <span className="font-medium text-gray-900 truncate">
                  {formatBudget(project)}
                </span>
              </div>
            </div>

            {/* Start Date */}
            <div className="col-span-2">
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                <span>{formatDate(project.start_date)}</span>
              </div>
            </div>

            {/* Duration */}
            <div className="col-span-2">
              <span className="text-sm text-gray-600">
                {project.duration_value && project.duration_unit 
                  ? `${project.duration_value} ${project.duration_unit}`
                  : project.duration || 'TBD'}
              </span>
            </div>

            {/* Actions */}
            <div className="col-span-1 flex justify-end gap-1">
              {isOwner ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onToggleActivation && onToggleActivation(project.id, project.is_activated);
                    }}
                    title={project.is_activated ? 'Deactivate' : 'Activate'}
                  >
                    {project.is_activated ? (
                      <Power className="w-4 h-4 text-green-600" />
                    ) : (
                      <PowerOff className="w-4 h-4 text-gray-400" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onEdit && onEdit(project);
                    }}
                    title="Edit"
                  >
                    <Edit className="w-4 h-4 text-gray-500" />
                  </Button>
                </>
              ) : (
                onToggleFavorite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onToggleFavorite(project.id);
                    }}
                    title={favorites.has(project.id) ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Star className={`w-4 h-4 ${favorites.has(project.id) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                  </Button>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectListView;
